import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ActionButtons, { DeleteButton } from '../../components/ActionButtons';
import { apiRequest } from '../../services/api';
import UsageInfo from '../../components/UsageInfo';
import { useKasa } from '../../context/KasaContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import ModalShell from '../../components/ModalShell';
import { brutTutar } from '../../utils/vergi';

const BOS_FORM = { ad: '', yetkili: '', telefon: '', email: '', adres: '', vergiDairesi: '', vergiNo: '', not: '', kdvOrani: 0, kdvDahilMi: false, konaklamaVergisi: 0, aktifMi: true };

const BOS_KURAL = { odaTipiId: '', fiyatTipi: 'gecelik', kisiBasiTip: 'sabit', fiyat: '', paraBirimi: 'TL', kisiDetayFiyatlar: [] };

function FormHata({ mesaj }) {
  if (!mesaj) return null;
  return <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mt-2 flex items-center gap-1 animate-shake">⚠️ {mesaj}</p>;
}

// ── Firma Ekle/Düzenle Modal ───────────────────────────────────────
function FirmaModal({ kayit, onKapat, onKaydet, kaydediliyor, hata }) {
  const yeni = !kayit?.firmaId;
  const [form, setForm] = useState({ ...BOS_FORM, ...(kayit || {}) });
  const [hatalar, setHatalar] = useState({});

  const INP = (h) => `w-full border-2 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 transition-all bg-white focus:outline-none ${h ? 'border-rose-400 bg-rose-50 focus:border-rose-500 shadow-rose-50 shadow-inner' : 'border-gray-100 focus:border-blue-400 focus:ring-4 focus:ring-blue-50 shadow-sm'}`;

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setHatalar(p => ({ ...p, [k]: null })); };

  return (
    <ModalShell
      icon="🏢"
      iconColor="from-blue-600 to-indigo-700"
      title={yeni ? 'Yeni Firma Tanımla' : 'Firma Bilgilerini Güncelle'}
      subtitle="Kurumsal anlaşmalar ve iletişim detayları"
      onClose={onKapat}
      size="lg"
      footer={
        <>
          <button onClick={onKapat} className="px-8 py-3.5 rounded-2xl border-2 border-gray-100 text-xs font-black text-gray-400 hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95">İPTAL</button>
          <button onClick={() => onKaydet(form)} disabled={kaydediliyor}
            className="px-10 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 disabled:opacity-60 text-white rounded-2xl text-xs font-black shadow-xl shadow-blue-200 transition-all hover:-translate-y-1 active:translate-y-0 tracking-widest uppercase">
            {kaydediliyor ? '⌛ İŞLEM...' : '💾 FİRMAYI KAYDET'}
          </button>
        </>
      }
    >
          {hata && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-5 py-4 text-xs text-rose-600 font-black uppercase tracking-widest leading-none animate-shake">⚠️ {hata}</div>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 leading-none">
            <div className="md:col-span-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">FİRMA ADI *</label>
              <input value={form.ad} onChange={e => set('ad', e.target.value)} className={INP(hatalar.ad)} placeholder="ÖRN: ABC TURİZM LTD. ŞTİ." autoFocus />
              <FormHata mesaj={hatalar.ad} />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">YETKİLİ KİŞİ</label>
              <input value={form.yetkili} onChange={e => set('yetkili', e.target.value)} className={INP(false)} placeholder="AD SOYAD" />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">TELEFON</label>
              <input value={form.telefon} onChange={e => set('telefon', e.target.value)} className={INP(false)} placeholder="05XX XXX XX XX" />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">E-POSTA</label>
              <input value={form.email} onChange={e => set('email', e.target.value)} className={INP(false)} placeholder="INFO@FIRMA.COM" />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">VERGİ NO</label>
              <input value={form.vergiNo} onChange={e => set('vergiNo', e.target.value)} className={INP(false)} />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">VERGİ DAİRESİ</label>
              <input value={form.vergiDairesi} onChange={e => set('vergiDairesi', e.target.value)} className={INP(false)} />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">KDV ORANI (%)</label>
              <div className="relative">
                <input type="number" min="0" max="100" step="1" value={form.kdvOrani} onChange={e => set('kdvOrani', parseFloat(e.target.value) || 0)} className={INP(false) + ' pr-12 text-right'} />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-black text-gray-300">%</span>
              </div>
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-3 cursor-pointer group w-fit select-none bg-gray-50 px-4 py-3.5 rounded-2xl border-2 border-gray-100 hover:bg-white transition-all w-full h-[52px] mt-7">
                <input type="checkbox" className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300" checked={form.kdvDahilMi} onChange={e => set('kdvDahilMi', e.target.checked)} />
                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest leading-none">FİYATLARA KDV DAHİL</span>
              </label>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">KONAKLAMA VERGİSİ (%)</label>
              <div className="relative">
                <input type="number" min="0" max="100" step="0.01" value={form.konaklamaVergisi} onChange={e => set('konaklamaVergisi', parseFloat(e.target.value) || 0)} className={INP(false) + ' pr-12 text-right'} />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-black text-gray-300">%</span>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">ADRES</label>
              <textarea value={form.adres} onChange={e => set('adres', e.target.value)} className={INP(false) + ' h-20 resize-none py-3'} />
            </div>
            <div className="md:col-span-2 flex items-center justify-between bg-gray-50 p-5 rounded-3xl border border-gray-100">
               <div>
                  <h4 className="text-sm font-black text-gray-800 uppercase tracking-tight">FİRMA DURUMU</h4>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Sistem Genelinde Aktif/Pasif</p>
               </div>
               <div className="flex items-center gap-4">
                  <span className={`text-[10px] font-black uppercase tracking-widest ${form.aktifMi ? 'text-emerald-600' : 'text-gray-400'}`}>{form.aktifMi ? 'AKTİF' : 'PASİF'}</span>
                  <button onClick={() => set('aktifMi', !form.aktifMi)}
                    className={`relative w-14 h-7 rounded-full transition-all duration-300 flex-shrink-0 shadow-inner ${form.aktifMi ? 'bg-emerald-500 shadow-emerald-200' : 'bg-gray-200 shadow-gray-100'}`}>
                    <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 transform-gpu ${form.aktifMi ? 'translate-x-7 rotate-180' : 'translate-x-0'}`} />
                  </button>
               </div>
            </div>
          </div>
    </ModalShell>
  );
}

// ── Fiyat Kuralı Modal ─────────────────────────────────────────────
function KuralModal({ kural, odaTipleri, paraBirimleri, dovizKurlari, onKapat, onKaydet, kaydediliyor }) {
  const yeni = !kural?.firmaFiyatKuraliId;
  const [form, setForm] = useState({ ...BOS_KURAL, ...(kural || {}) });
  const INP = "w-full border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-50 transition-all bg-white shadow-sm";

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Yeni kural eklerken default para birimini TRY'ye çek (API 'TRY' döner, BOS_KURAL 'TL' içeriyor)
  useEffect(() => {
    if (yeni && paraBirimleri.length > 0) {
      const tryPb = paraBirimleri.find(pb => pb.tcmbKod === 'TRY' || pb.tcmbKod === 'TL');
      if (tryPb) setForm(p => ({ ...p, paraBirimi: tryPb.tcmbKod }));
    }
  }, [paraBirimleri]); // eslint-disable-line react-hooks/exhaustive-deps

  const kurDegeri = (form.paraBirimi && form.paraBirimi !== 'TL' && form.paraBirimi !== 'TRY' && dovizKurlari?.[form.paraBirimi] > 0)
    ? dovizKurlari[form.paraBirimi] : 0;

  const detayEkle = () => {
    const s = form.kisiDetayFiyatlar.length ? Math.max(...form.kisiDetayFiyatlar.map(d => d.kisiSirasi)) + 1 : 1;
    set('kisiDetayFiyatlar', [...form.kisiDetayFiyatlar, { kisiSirasi: s, fiyat: '' }]);
  };
  const detayGuncelle = (idx, f, v) => set('kisiDetayFiyatlar', form.kisiDetayFiyatlar.map((d, i) => i === idx ? { ...d, [f]: v } : d));

  return (
    <ModalShell
      icon="💰"
      iconColor="from-purple-600 to-indigo-700"
      title={yeni ? 'Yeni Fiyat Kuralı' : 'Kuralı Güncelle'}
      subtitle="Firmaya özel gecelik veya kişi başı fiyatlandırma"
      onClose={onKapat}
      size="md"
      footer={
        <>
          <button onClick={onKapat} className="px-8 py-3.5 rounded-2xl border-2 border-gray-100 text-xs font-black text-gray-400 hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95 leading-none">İPTAL</button>
          <button onClick={() => onKaydet(form)} disabled={kaydediliyor}
            className="px-10 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 disabled:opacity-60 text-white rounded-2xl text-xs font-black shadow-xl shadow-purple-200 transition-all hover:-translate-y-1 active:translate-y-0 tracking-widest uppercase">
            {kaydediliyor ? '⌛ İŞLEM...' : '💾 KURALI KAYDET'}
          </button>
        </>
      }
    >
        <div className="space-y-7 leading-none text-left">
          <div className="text-left">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">KAPSAM / ODA TİPİ</label>
            <select value={form.odaTipiId} onChange={e => set('odaTipiId', e.target.value)} className={INP + ' cursor-pointer uppercase'}>
               <option value="">TÜM ODA TİPLERİ (GENEL)</option>
               {odaTipleri.map(o => <option key={o.odaTipiId} value={o.odaTipiId}>{o.ad}</option>)}
            </select>
          </div>

          <div className="text-left select-none">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-4 ml-1">FİYATLANDIRMA TİPİ</label>
            <div className="grid grid-cols-2 gap-4 mb-6">
               {[ { id: 'gecelik', icon: '🌙', label: 'GECELİK' }, { id: 'kisi-basi', icon: '👤', label: 'KİŞİ BAZLI' } ].map(t => (
                 <button key={t.id} onClick={() => set('fiyatTipi', t.id)}
                   className={`p-5 rounded-[1.8rem] border-2 transition-all duration-300 transform-gpu flex flex-col items-center gap-2 ${form.fiyatTipi === t.id ? 'border-purple-500 bg-purple-600 text-white shadow-xl shadow-purple-100 scale-105' : 'border-gray-50 bg-gray-50/50 text-gray-400 hover:border-purple-200'}`}>
                   <span className="text-xl leading-none">{t.icon}</span>
                   <span className="font-black text-[10px] uppercase tracking-widest">{t.label}</span>
                 </button>
               ))}
            </div>

            {form.fiyatTipi === 'kisi-basi' && (
              <div className="flex bg-gray-100/50 p-1 rounded-2xl animate-fade-in">
                 {['sabit', 'kademeli'].map(t => (
                   <button key={t} onClick={() => set('kisiBasiTip', t)}
                     className={`flex-1 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${form.kisiBasiTip === t ? 'bg-white text-purple-600 shadow-sm scale-[1.02]' : 'text-gray-400 hover:text-gray-600'}`}>
                     {t === 'sabit' ? 'TEK (SABİT) FİYAT' : 'KADEMELİ FİYAT'}
                   </button>
                 ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
             <div className="text-left select-none">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">PARA BİRİMİ</label>
                <select value={form.paraBirimi} onChange={e => set('paraBirimi', e.target.value)} className={INP + ' cursor-pointer'}>
                  {paraBirimleri.length > 0 ? paraBirimleri.map(pb => <option key={pb.tcmbKod} value={pb.tcmbKod}>{pb.tcmbKod}</option>) : <option value="TL">TL</option>}
                </select>
             </div>
             {(form.fiyatTipi === 'gecelik' || (form.fiyatTipi === 'kisi-basi' && form.kisiBasiTip === 'sabit')) && (
               <div className="relative animate-fade-in">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">
                    {form.fiyatTipi === 'gecelik' ? 'GECELİK FİYAT (NET)' : 'KİŞİ BAŞI FİYAT (SABİT)'}
                  </label>
                  <input type="number" value={form.fiyat} onChange={e => set('fiyat', e.target.value)} className={INP + ' pr-12 text-center text-lg'} />
                  <span className="absolute right-5 bottom-4 font-black text-gray-300">₺</span>
               </div>
             )}
          </div>

          {(form.fiyatTipi === 'kisi-basi' && form.kisiBasiTip === 'kademeli') && (
            <div className="space-y-6 animate-slide-up text-left">
               <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">KİŞİ KADEMELERİ (ÖRN: 1.KİŞİ: X, 2.KİŞİ: Y)</h4>
                  <button onClick={detayEkle} className="text-[9px] font-black bg-purple-50 text-purple-600 px-3 py-1.5 rounded-lg border border-purple-100 hover:bg-purple-100 transition-all uppercase tracking-widest">＋ KİŞİ EKLE</button>
               </div>
               <div className="space-y-3">
                  {form.kisiDetayFiyatlar.map((d, i) => (
                    <div key={i} className="flex items-center gap-3 bg-gray-50/50 p-2 rounded-2xl border border-gray-100 group">
                       <span className="w-16 text-[10px] font-black text-purple-500 text-center uppercase">{d.kisiSirasi}. KİŞİ</span>
                       <div className="flex-1 flex flex-col gap-1">
                         <input type="number" value={d.fiyat} onChange={e => detayGuncelle(i, 'fiyat', e.target.value)} className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm font-black focus:border-purple-400 focus:outline-none w-full" placeholder="0.00" />
                         {kurDegeri > 0 && d.fiyat > 0 && (
                           <span className="text-[10px] text-purple-400 font-bold text-right pr-2">≈ {(parseFloat(d.fiyat) * kurDegeri).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺</span>
                         )}
                       </div>
                       <button onClick={() => set('kisiDetayFiyatlar', form.kisiDetayFiyatlar.filter((_, idx) => idx !== i))} className="w-10 h-10 flex items-center justify-center text-rose-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all">✕</button>
                    </div>
                  ))}
               </div>
            </div>
          )}
        </div>
    </ModalShell>
  );
}

export default function Firmalar() {
  const { dovizKurlari } = useKasa();
  const [firmalar, setFirmalar] = useState([]);
  const [odaTipleri, setOdaTipleri] = useState([]);
  const [paraBirimleri, setParaBirimleri] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [detayFirma, setDetayFirma] = useState(null);
  const [modal, setModal] = useState(null);
  const [kuralModal, setKuralModal] = useState(null);
  const [silOnay, setSilOnay] = useState(null);
  const [kuralSilOnay, setKuralSilOnay] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const [fd, od, pb] = await Promise.all([apiRequest('firmalar'), apiRequest('OdaTipi'), apiRequest('ParaBirimi')]);
      setFirmalar(fd || []); setOdaTipleri(od || []); setParaBirimleri(pb || []);
    } catch { /* sessiz */ }
    setYukleniyor(false);
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  const handleKaydet = async (data) => {
    if (!data.ad.trim()) { setHata('Firma adı zorunludur.'); return; }
    setKaydediliyor(true); setHata(null);
    try {
      if (data.firmaId) await apiRequest('firmalar', 'PUT', data);
      else await apiRequest('firmalar', 'POST', data);
      await yukle(); 
      // Detay panelini güncelle (State senkronizasyonu)
      const guncelListe = await apiRequest('firmalar');
      const guncelFirma = guncelListe.find(f => f.firmaId === (data.firmaId || detayFirma?.firmaId));
      if (guncelFirma) setDetayFirma(guncelFirma);
      setModal(null);
    } catch { setHata('Kaydedilemedi.'); }
    setKaydediliyor(false);
  };

  const handleKuralKaydet = async (data) => {
     setKaydediliyor(true);
      try {
         let fiyatVal = parseFloat(data.fiyat) || 0;

         const dto = { ...data, odaTipiId: data.odaTipiId ? parseInt(data.odaTipiId) : null, fiyat: fiyatVal, firmaId: detayFirma.firmaId };
        if (data.firmaFiyatKuraliId) await apiRequest('firmalar/kural', 'PUT', { firmaFiyatKuraliId: data.firmaFiyatKuraliId, ...dto });
        else await apiRequest('firmalar/kural', 'POST', dto);
        await yukle();
        const guncel = (await apiRequest('firmalar')).find(f => f.firmaId === detayFirma.firmaId);
        setDetayFirma(guncel || null);
        setKuralModal(null);
     } catch (e) { alert(e?.message || 'Kural hatası.'); }
     setKaydediliyor(false);
  };

  const handleSil = async (id) => { await apiRequest(`firmalar/${id}`, 'DELETE'); if (detayFirma?.firmaId === id) setDetayFirma(null); setSilOnay(null); yukle(); };
  const handleKuralSil = async (id) => { 
    await apiRequest(`firmalar/kural/${id}`, 'DELETE'); 
    await yukle(); 
    const guncel = (await apiRequest('firmalar')).find(f => f.firmaId === detayFirma.firmaId);
    setDetayFirma(guncel || null);
    setKuralSilOnay(null); 
  };

  const aktifPasifToggle = async (firma, e) => {
    e.stopPropagation();
    await apiRequest(`firmalar/${firma.firmaId}/aktif?aktif=${!firma.aktifMi}`, 'PATCH');
    yukle();
  };

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn leading-none" style={{ transform: 'translateZ(0)' }}>
      <PageHeader
        icon="🏢"
        iconColor="from-blue-600 to-indigo-700"
        iconShadow="shadow-blue-200"
        title="Anlaşmalı Firmalar"
        subtitle="Kurumsal fiyat anlaşmaları ve kural yönetimi"
      >
        <button onClick={() => { setHata(null); setModal('yeni'); }}
          className="group flex-shrink-0 flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-10 py-4 rounded-2xl text-[10px] font-black shadow-2xl shadow-blue-100 transition-all hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest relative overflow-hidden leading-none">
          <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
          <span className="relative text-xl leading-none">＋</span> <span className="relative leading-none">YENİ FİRMA EKLE</span>
        </button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 text-left leading-none items-start">
        {/* LİSTE SOL */}
        <div className="lg:col-span-4 space-y-4 leading-none">
           <div className="flex items-center justify-between px-2 mb-2">
              <h2 className="text-[11px] font-black text-gray-400 uppercase tracking-widest leading-none">FİRMA LİSTESİ ({firmalar.length})</h2>
           </div>
           <div className="space-y-3 leading-none no-scrollbar max-h-[80vh] overflow-y-auto pt-4">
             {yukleniyor ? (
               <LoadingSpinner fullPage text="Kayıtlar Okunuyor..." />
             ) : firmalar.length === 0 ? (
               <EmptyState icon="🏢" text="Henüz bir firma tanımlanmamış" />
             ) : firmalar.map(f => {
               const aktif = detayFirma?.firmaId === f.firmaId;
               return (
                 <div key={f.firmaId} onClick={() => setDetayFirma(f)}
                   className={`group relative p-5 rounded-[2rem] border-2 transition-all duration-300 transform-gpu cursor-pointer overflow-hidden ${aktif ? 'border-blue-500 bg-white shadow-xl shadow-blue-50 -translate-y-1' : 'border-white bg-white hover:border-blue-100 hover:shadow-lg'}`}>
                    {aktif && <div className="absolute left-0 top-0 bottom-0 w-2 bg-blue-600"></div>}
                    <div className="flex items-start gap-4 leading-none">
                       <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl shadow-inner border leading-none transform-gpu group-hover:scale-110 transition-transform ${f.aktifMi ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-gray-50 text-gray-300 border-gray-100 uppercase'}`}>{f.ad[0]}</div>
                       <div className="min-w-0 flex-1 leading-none text-left">
                          <h3 className="font-black text-gray-700 text-sm md:text-base leading-none uppercase truncate">{f.ad}</h3>
                          <div className="flex items-center gap-3 mt-2 flex-wrap leading-none">
                             <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none">{f.yetkili || 'YETKİLİ BELİRTİLMEMİŞ'}</span>
                             {f.fiyatKurallari?.length > 0 && <span className="text-[9px] font-black bg-blue-50 text-blue-600 px-2 py-1 rounded-lg leading-none border border-blue-100">{f.fiyatKurallari.length} KURAL</span>}
                          </div>
                       </div>
                       <button onClick={(e) => aktifPasifToggle(f, e)} className={`w-3 h-3 rounded-full flex-shrink-0 animate-pulse mt-2 shadow-inner border ${f.aktifMi ? 'bg-emerald-500 shadow-emerald-200 border-emerald-400' : 'bg-gray-300 border-gray-300'}`}></button>
                    </div>
                 </div>
               );
             })}
           </div>
        </div>

        {/* DETAY SAĞ */}
        <div className="lg:col-span-8 leading-none">
           <div className="bg-white rounded-[3rem] border border-gray-100 shadow-xl overflow-hidden min-h-[500px] flex flex-col leading-none transform-gpu animate-slide-up">
              {!detayFirma ? (
                <div className="flex-1 flex flex-col items-center justify-center py-32 text-center text-[10px] font-black text-gray-300 uppercase tracking-widest italic lowercase tracking-tight leading-loose opacity-60">
                   <div className="text-8xl mb-6 transform-gpu animate-bounce-subtle">📋</div>
                   GÖRÜNTÜLEMEK VE KURAL EKLEMEK İÇİN<br/>SOL LİSTEDEN BİR FİRMA SEÇİNİZ
                </div>
              ) : (
                <div className="flex-1 flex flex-col leading-none text-left">
                   {/* Firma Panel Header */}
                   <div className="p-8 md:p-12 bg-gradient-to-br from-slate-800 to-slate-900 text-white relative overflow-hidden leading-none text-left">
                      <div className="absolute -right-20 -bottom-20 text-[15rem] opacity-5 pointer-events-none transform-gpu rotate-12 leading-none font-black uppercase">{detayFirma.ad[0]}</div>
                      <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8 leading-none text-left">
                         <div className="flex items-center gap-6 leading-none text-left">
                             <div className="w-20 h-20 bg-white/10 backdrop-blur-xl border border-white/20 rounded-[2rem] flex items-center justify-center text-4xl shadow-2xl leading-none">{detayFirma.ad[0]}</div>
                             <div className="leading-none text-left">
                                <h2 className="text-2xl md:text-3xl font-black tracking-tight leading-none uppercase mb-3">{detayFirma.ad}</h2>
                                <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-white/60 uppercase tracking-widest leading-none">
                                   <span className="flex items-center gap-1.5"><span className="text-sky-400 text-lg">👤</span> {detayFirma.yetkili || 'YETKİLİ YOK'}</span>
                                   <span className="flex items-center gap-1.5"><span className="text-sky-400 text-lg">📞</span> {detayFirma.telefon || 'NUMARA YOK'}</span>
                                   <span className="flex items-center gap-1.5"><span className="text-sky-400 text-lg">✉️</span> {detayFirma.email || 'E-POSTA YOK'}</span>
                                </div>
                             </div>
                         </div>
                         <div className="flex flex-wrap items-center gap-3 leading-none">
                            <button onClick={() => { setModal(detayFirma); setHata(null); }} className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border border-white/10 leading-none">BİLGİLERİ DÜZENLE</button>
                            <DeleteButton onClick={() => setSilOnay(detayFirma.firmaId)} label="Firmayı Sil" />
                         </div>
                      </div>
                   </div>

                   {/* Kurallar Section */}
                   <div className="p-8 md:p-12 space-y-10 leading-none text-left">
                      <div className="flex flex-col md:flex-row items-center justify-between gap-6 leading-none">
                         <div>
                            <h3 className="text-xl font-black text-gray-800 tracking-tight uppercase leading-none">Özel Fiyat Kuralları</h3>
                            <p className="text-[10px] font-black text-gray-400 mt-2 uppercase tracking-widest leading-none">BU FİRMA İÇİN TANIMLI ({detayFirma.fiyatKurallari?.length || 0}) ANLAŞMA KURALI</p>
                         </div>
                         <button onClick={() => setKuralModal('yeni')} className="bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white px-8 py-4 rounded-2xl text-[10px] font-black shadow-xl shadow-purple-200 transition-all hover:scale-105 active:scale-95 leading-none uppercase tracking-[0.2em]">＋ YENİ KURAL TANIMLA</button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 leading-none text-left">
                         {(!detayFirma.fiyatKurallari || detayFirma.fiyatKurallari.length === 0) ? (
                            <div className="md:col-span-2 py-20 text-center border-4 border-dashed border-gray-50 rounded-[2.5rem] flex flex-col items-center leading-none">
                               <div className="text-6xl grayscale opacity-10 mb-4 transform-gpu -rotate-12 leading-none">💰</div>
                               <p className="font-black text-[10px] text-gray-300 uppercase tracking-widest leading-none italic lowercase tracking-tight italic">HENÜZ ÖZEL BİR KURAL TANIMLANMAMIŞ</p>
                            </div>
                         ) : (
                           detayFirma.fiyatKurallari.map(k => (
                             <div key={k.firmaFiyatKuraliId} className="group relative bg-gray-50/50 hover:bg-purple-50/20 border-2 border-gray-100 hover:border-purple-200 rounded-[2.5rem] p-6 transition-all duration-300 transform-gpu shadow-sm hover:-translate-y-1 leading-none text-left">
                                <div className="flex items-start justify-between gap-4 mb-5 leading-none">
                                   <div className="leading-none text-left flex-1 min-w-0">
                                      <p className="text-[9px] font-black text-purple-400 uppercase tracking-widest mb-1.5 opacity-60 leading-none truncate">{k.odaTipiAd || 'TÜM ODA TİPLERİ'}</p>
                                      <h4 className="font-black text-gray-700 text-base leading-tight uppercase leading-none truncate">
                                         {k.fiyatTipi === 'gecelik' ? 'GECELİK SABİT' : (k.kisiBasiTip === 'sabit' ? 'KİŞİ BAŞI SABİT' : 'KİŞİ BAZLI KADEMELİ')}
                                      </h4>
                                   </div>
                                   <div className="text-right flex flex-col items-end leading-none flex-shrink-0">
                                      <span className="font-black text-purple-600 text-xl leading-none">
                                         {(k.fiyatTipi === 'gecelik' || k.kisiBasiTip === 'sabit') ? (() => {
                                           const toplam = brutTutar(k.fiyat, detayFirma.kdvOrani, detayFirma.kdvDahilMi, detayFirma.konaklamaVergisi);
                                           return `${(toplam === k.fiyat ? k.fiyat : +toplam.toFixed(4))} ${k.paraBirimi}`;
                                         })() : (k.paraBirimi + ' KADEMELİ')}
                                      </span>
                                      {(detayFirma.kdvOrani > 0 || detayFirma.konaklamaVergisi > 0) && (k.fiyatTipi === 'gecelik' || k.kisiBasiTip === 'sabit') && (
                                         <span className="text-[10px] text-purple-400 mt-1 font-bold">
                                            {detayFirma.kdvDahilMi ? `KDV %${detayFirma.kdvOrani||0} DAHİL` : `+%${detayFirma.kdvOrani||0} KDV`}
                                            {detayFirma.konaklamaVergisi > 0 && ` + %${detayFirma.konaklamaVergisi} Kon.Verg.`}
                                            {detayFirma.kdvDahilMi && ` (NET: ${k.fiyat})`}
                                         </span>
                                      )}
                                   </div>
                                </div>
                                
                                {k.fiyatTipi === 'kisi-basi' && k.kisiBasiTip !== 'sabit' && (
                                  <div className="flex flex-col gap-2 mb-6 leading-none">
                                    <div className="flex flex-wrap gap-2">
                                      {(k.kisiDetayFiyatlar || []).sort((a,b) => a.kisiSirasi - b.kisiSirasi).map(d => (
                                         <span key={d.kisiSirasi} className="text-[9px] font-black bg-white border border-purple-100 text-purple-500 px-2 py-1 rounded-lg leading-none">
                                            {d.kisiSirasi}. KİŞİ: {(() => { const t = brutTutar(d.fiyat, detayFirma.kdvOrani, detayFirma.kdvDahilMi, detayFirma.konaklamaVergisi); return t === d.fiyat ? d.fiyat : t; })()} {k.paraBirimi}
                                         </span>
                                      ))}
                                    </div>
                                    {(detayFirma.kdvOrani > 0 || detayFirma.konaklamaVergisi > 0) && (
                                       <span className="text-[9px] text-purple-400 font-bold ml-1">
                                          ({detayFirma.kdvDahilMi ? `KDV %${detayFirma.kdvOrani||0} DAHİL` : `+%${detayFirma.kdvOrani||0} KDV HARİÇ`}
                                          {detayFirma.konaklamaVergisi > 0 ? `, +%${detayFirma.konaklamaVergisi} Kon.Verg.` : ''})
                                       </span>
                                    )}
                                  </div>
                                )}

                                <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-5 leading-none">
                                   <ActionButtons onEdit={() => setKuralModal(k)} onDelete={() => setKuralSilOnay(k.firmaFiyatKuraliId)} />
                                </div>
                             </div>
                           ))
                         )}
                      </div>

                      {/* Not & Vergi Detay */}
                      {(detayFirma.not || detayFirma.vergiNo) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-10 border-t border-gray-100 leading-none">
                           {detayFirma.vergiNo && (
                             <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 space-y-4 leading-none text-left">
                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">FATURA DETAYLARI</h4>
                                <div className="space-y-3 font-black text-xs text-gray-600 leading-none">
                                   <p className="flex justify-between"><span>V. DAİRESİ:</span> <span>{detayFirma.vergiDairesi || '—'}</span></p>
                                   <p className="flex justify-between"><span>VERGİ NO:</span> <span>{detayFirma.vergiNo}</span></p>
                                   <p className="flex justify-between pt-2 mt-2 border-t border-gray-200"><span>KDV ORANI:</span> <span>%{detayFirma.kdvOrani || 0} ({detayFirma.kdvDahilMi ? 'DAHİL' : 'HARİÇ'})</span></p>
                                   {(detayFirma.konaklamaVergisi > 0) && (
                                     <p className="flex justify-between"><span>KONAKLAMA VERGİSİ:</span> <span>%{detayFirma.konaklamaVergisi}</span></p>
                                   )}
                                </div>
                             </div>
                           )}
                           {detayFirma.not && (
                             <div className="bg-amber-50/30 p-6 rounded-3xl border border-amber-100 text-left leading-none">
                                <h4 className="text-[10px] font-black text-amber-600 uppercase tracking-widest leading-none mb-4">📝 ÖZEL NOTLAR</h4>
                                <p className="text-xs font-bold text-amber-900 leading-relaxed uppercase tracking-tight italic opacity-80">{detayFirma.not}</p>
                             </div>
                           )}
                        </div>
                      )}
                   </div>
                </div>
              )}
           </div>
        </div>
      </div>

      <UsageInfo
        color="blue"
        description="Firmalar modülü, otelinizin acenteler veya kurumsal müşterilerle yaptığı özel fiyat anlaşmalarını merkezi bir noktadan kurgulamanızı sağlar."
        scenarios={[
          { title: "Gecelik Sabit Fiyat", content: "Belirli bir şirkete, standart oda tipleri için yıl boyunca geçerli olacak sabit bir Euro/Dolar fiyatı atayabilirsiniz." },
          { title: "Acentelere Özel Kademeler", content: "Otele gelecek kişi sayısına göre artan veya azalan kademeli fiyatlandırma kuralları oluşturabilirsiniz." },
          { title: "Vergi & İletişim Arşivi", content: "Sözleşme notlarını, fatura adreslerini ve vergi bilgilerini firma kartı üzerinde saklayarak operasyonu hızlandırabilirsiniz." }
        ]}
      />

      {modal !== null && createPortal(
        <FirmaModal kayit={modal === 'yeni' ? null : modal} onKapat={() => setModal(null)} onKaydet={handleKaydet} kaydediliyor={kaydediliyor} hata={hata} />,
        document.body
      )}

      {kuralModal !== null && createPortal(
        <KuralModal kural={kuralModal === 'yeni' ? null : kuralModal} odaTipleri={odaTipleri} paraBirimleri={paraBirimleri} dovizKurlari={dovizKurlari} onKapat={() => setKuralModal(null)} onKaydet={handleKuralKaydet} kaydediliyor={kaydediliyor} />,
        document.body
      )}

      {(silOnay || kuralSilOnay) && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4 backdrop-blur-md animate-fade-in shadow-2xl leading-none font-black uppercase text-center">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 w-full max-w-sm border border-gray-100 leading-none">
            <div className="w-24 h-24 bg-rose-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-5xl shadow-xl shadow-rose-100/50 animate-bounce-subtle leading-none">🗑️</div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight leading-none uppercase">{kuralSilOnay ? 'Kuralı Sil' : 'Firmayı Sil'}</h3>
            <p className="text-[11px] font-black text-gray-400 mt-5 mb-10 leading-relaxed uppercase tracking-tight leading-loose italic">Bu işlem geri alınamaz ve tüm ilişkili veriler (kural varsa kurallar) temizlenecektir. Emin misiniz?</p>
            <div className="flex gap-4">
              <button onClick={() => { setSilOnay(null); setKuralSilOnay(null); }} className="flex-1 py-4 bg-gray-50 text-gray-600 rounded-2xl text-[10px] font-black hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95 leading-none">İPTAL</button>
              <button onClick={() => silOnay ? handleSil(silOnay) : handleKuralSil(kuralSilOnay)} className="flex-1 py-4 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-100 transition-all uppercase tracking-widest transform-gpu active:scale-95 leading-none font-black text-lg">EVET, SİL</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        .animate-shake { animation: shake 0.3s ease-in-out infinite; animation-iteration-count: 2; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes bounceSubtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .animate-bounce-subtle { animation: bounceSubtle 2s infinite; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
